import React, { useState } from "react";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Input from "../../common/Input";
import Button from "../../common/Button";
import apiAxios from "../../../services/apiAxios";
import CloseBorder from "../../common/icons/CloseBorder";

const fieldSchemas = {
  contactNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^\d{10}$/, "Contact number must be exactly 10 digits"),
  pin: Yup.string()
    .required("PIN is required")
    .matches(/^\d{5}$/, "PIN must be a 5-digit."),
};

const AddDoctorModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ contactNumber: "", pin: "" });
  const [formErrors, setFormErrors] = useState({ contactNumber: "", pin: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    try {
      await fieldSchemas[name].validate(value);
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setFormErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.contactNumber) errors.contactNumber = "Contact Number is required";
    if (!formData.pin) {
      errors.pin = "PIN is required";
    } else if (!/^\d{5}$/.test(formData.pin)) {
      errors.pin = "PIN must be a 5-digit";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;
    if (!navigator.onLine) {
      toast.error("No internet connection.");
      return;
    }

    setLoading(true);
    try {
      // 1. Authenticate
      console.log("Starting loginRes"); const loginRes = await apiAxios.post("api/auth/doctor/login", {
        contactNumber: formData.contactNumber,
        pin: formData.pin,
      });
      const { token, refreshToken } = loginRes.data;

      // 2. Fetch details using apiAxios by temporarily swapping the token
      const previousToken = localStorage.getItem("token");
      localStorage.setItem("token", token);
      
      let detailsRes;
      try {
        detailsRes = await apiAxios.get("api/doctor/get-doctor-detail");
      } finally {
        if (previousToken) {
          localStorage.setItem("token", previousToken);
        } else {
          localStorage.removeItem("token");
        }
      }
      
      const doctor = detailsRes.data?.doctor;
      if (!doctor) throw new Error("Could not fetch doctor details");

      // 3. Update localStorage
      const loggedInDoctors = JSON.parse(localStorage.getItem("loggedInDoctors") || "[]");
      
      // Prevent duplicates based on DID
      if (loggedInDoctors.some(d => d.DID === doctor.DID)) {
        toast.error("This account is already logged in.");
        setLoading(false);
        return;
      }

      const newAccount = {
        token,
        refreshToken,
        DID: doctor.DID,
        name: doctor.personalInfo?.name || "",
        surname: doctor.personalInfo?.surname || "",
        clinicName: doctor.personalInfo?.clinicName || doctor.clinicAddress?.premisesName || doctor.ClinicAddress?.premisesName || "",
        address: [
          doctor.clinicAddress?.areaRoad || doctor.ClinicAddress?.areaRoad, 
          doctor.clinicAddress?.city || doctor.ClinicAddress?.city
        ].filter(Boolean).join(", ") || "",
        phoneNumber: doctor.contactNumber,
        isPrimary: false,
        isOperational: false,
        __v: 2,
      };

      loggedInDoctors.push(newAccount);
      localStorage.setItem("loggedInDoctors", JSON.stringify(loggedInDoctors));
      
      toast.success("Account added successfully!");
      onSuccess();
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "An error occurred";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[450px] relative overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-gray-100">
          <h3 className="text-[18px] font-semibold text-primary-600">Add Doctor Account</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors">
            <CloseBorder width={24} height={24} />
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <Input
              placeholder="Enter Contact Number"
              type="number"
              name="contactNumber"
              value={formData.contactNumber}
              labelName="Contact Number"
              onChange={handleChange}
              labelClassName="block text-primary-400 text-[13px] leading-[30px]"
              className="mb-2 border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[32px] border-b border-gray-200 focus:border-primary-600 rounded-none px-0"
              isError={!!formErrors.contactNumber}
              errorMessage={formErrors.contactNumber}
            />
          </div>
          <div className="mb-8">
            <Input
              placeholder="Enter PIN"
              type="password"
              name="pin"
              value={formData.pin}
              labelName="PIN (5-digit)"
              onChange={handleChange}
              labelClassName="block text-primary-400 text-[13px] leading-[20px]"
              className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[32px] border-b border-gray-200 focus:border-primary-600 rounded-none px-0"
              isError={!!formErrors.pin}
              errorMessage={formErrors.pin}
            />
          </div>

          <Button
            className="w-full bg-primary-600 h-[44px] text-white py-2 px-5 text-[14px] font-medium rounded-lg flex items-center justify-center transition-all hover:bg-primary-700 shadow-md"
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? "Adding..." : "Add Account"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddDoctorModal;
